import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../utils/format';
import { useUserConfig } from '../hooks/useUserConfig';
import { usePagination } from '../hooks/usePagination';
import { useTableSort } from '../hooks/useTableSort';
import PaginationControls from '../components/PaginationControls';
import ErrorLoading from '../components/ErrorLoading';

function EventDetails({ token, backendUrl }) {
  const { t } = useTranslation();
  const { dateEventId } = useParams();
  const [eventData, setEventData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const pagination = usePagination('events.pageSize', 50);
  const { sortField, sortDirection, sortableHeader } = useTableSort('created_at', 'asc');
  const [chartYField, setChartYField] = useUserConfig('events.chartYAxis', 'home_price');

  const chartOptions = useMemo(() => [
    { key: 'home_price', name: t('events.homeOdds') },
    { key: 'away_price', name: t('events.awayOdds') },
    { key: 'home_1x2_price', name: t('events.home1x2Odds') },
    { key: 'draw_1x2_price', name: t('events.draw1x2Odds') },
    { key: 'away_1x2_price', name: t('events.away1x2Odds') },
    { key: 'home_handicap', name: t('events.homeHandicap') },
    { key: 'away_handicap', name: t('events.awayHandicap') },
    { key: 'total_goal_over_price_2_point_5', name: t('events.totalGoalOver25') },
    { key: 'total_goal_under_price_2_point_5', name: t('events.totalGoalUnder25') },
  ], [t]);

  useEffect(() => {
    if (!token) {
      setError(t('events.authMissing'));
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const fetchEventData = async () => {
      try {
        setLoading(true);
        const authHeader = { Authorization: `Bearer ${token}` };
        const countResponse = await axios.get(
          `${backendUrl}/sg_pools/events/${dateEventId}/count`,
          { headers: authHeader, signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        pagination.setTotalCount(countResponse.data.count || 0);

        const skip = (pagination.currentPage - 1) * pagination.pageSize;
        const response = await axios.get(`${backendUrl}/sg_pools/events/${dateEventId}`, {
          headers: authHeader,
          params: { skip, limit: pagination.pageSize },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        const data = Array.isArray(response.data.data) ? response.data.data : [];
        setEventData(data);
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return;
        console.error('取得賽事資料錯誤:', err.response?.data, err.message);
        setError(err.response?.data?.error || err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchEventData();
    return () => controller.abort();
    // pagination object 是 memoized，只依賴 setTotalCount/currentPage/pageSize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, backendUrl, dateEventId, pagination.currentPage, pagination.pageSize, t]);

  useEffect(() => {
    const sorted = [...eventData].sort((a, b) => {
      const aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
    setSortedData(sorted);
  }, [eventData, sortField, sortDirection]);

  const chartData = useMemo(() => {
    if (!sortedData || sortedData.length === 0 || !chartYField) return [];
    return sortedData
      .map((item, index) => {
        const value = parseFloat(item[chartYField]);
        let timeLabel = '';
        if (item.created_at) {
          timeLabel = new Date(item.created_at).toLocaleTimeString('zh-TW', {
            timeZone: 'Asia/Singapore',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          });
        }
        return {
          name: timeLabel || `Row ${index}`,
          value: isNaN(value) ? null : value,
        };
      })
      .filter((d) => d.value !== null);
  }, [sortedData, chartYField]);

  const currentYFieldName =
    chartOptions.find((opt) => opt.key === chartYField)?.name || t('events.tooltipValue');

  const downloadCSV = useCallback(async () => {
    if (!token) {
      setError(t('events.authMissing'));
      return;
    }
    try {
      const response = await axios.get(`${backendUrl}/sg_pools/events/${dateEventId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip: 0, limit: -1 },
      });
      const allEventData = Array.isArray(response.data.data) ? response.data.data : [];

      const sorted = [...allEventData].sort((a, b) => {
        const aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });

      const headers = [
        t('events.homeHandicap'), t('events.awayHandicap'),
        t('events.homeOdds'), t('events.awayOdds'),
        t('events.home1x2Odds'), t('events.draw1x2Odds'), t('events.away1x2Odds'),
        t('events.halfHomeHandicap'), t('events.halfAwayHandicap'),
        t('events.halfHomeOdds'), t('events.halfAwayOdds'),
        t('events.totalGoalOver15'), t('events.totalGoalUnder15'),
        t('events.totalGoalOver25'), t('events.totalGoalUnder25'),
        t('events.totalGoalOver35'), t('events.totalGoalUnder35'),
        t('common.createdAt'),
      ];

      const rows = sorted.map((item) => [
        item.home_handicap ?? '',
        item.away_handicap ?? '',
        item.home_price ?? '',
        item.away_price ?? '',
        item.home_1x2_price ?? '',
        item.draw_1x2_price ?? '',
        item.away_1x2_price ?? '',
        item.home_half_goal_handicap ?? '',
        item.away_half_goal_handicap ?? '',
        item.home_half_goal_price ?? '',
        item.away_half_goal_price ?? '',
        item.total_goal_over_price_1_point_5 ?? '',
        item.total_goal_under_price_1_point_5 ?? '',
        item.total_goal_over_price_2_point_5 ?? '',
        item.total_goal_under_price_2_point_5 ?? '',
        item.total_goal_over_price_3_point_5 ?? '',
        item.total_goal_under_price_3_point_5 ?? '',
        formatDateTime(item.created_at),
      ]);

      const csvContent = [
        '﻿' + headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `event_${dateEventId}_data_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('下載 CSV 錯誤:', err);
      setError(t('common.csvDownloadError'));
    }
  }, [token, backendUrl, dateEventId, sortDirection, t]);

  return (
    <div className="min-vh-100 bg-body-tertiary">
      <div className="container py-4">
        <h2 className="mb-4">{t('events.detailTitle', { id: dateEventId })}</h2>
        {sortedData && sortedData.length > 0 && (
          <div className="card shadow-lg p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title">{t('events.trendChart')}</h5>
              <div className="d-flex align-items-center">
                <label htmlFor="chartYFieldSelect" className="form-label me-2 mb-0">
                  {t('events.yAxisSelect')}
                </label>
                <select
                  id="chartYFieldSelect"
                  value={chartYField}
                  onChange={(e) => setChartYField(e.target.value)}
                  className="form-select"
                  style={{ width: '300px' }}
                >
                  {chartOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" interval="preserveStartEnd" />
                <YAxis domain={['auto', 'auto']} />
                <Tooltip
                  labelFormatter={(label) => `${t('events.tooltipTime')}: ${label}`}
                  formatter={(value) => [`${t('events.tooltipValue')}: ${value.toFixed(4)}`, currentYFieldName]}
                />
                <Legend />
                <Line type="monotone" dataKey="value" name={currentYFieldName} stroke="#8884d8" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>{t('events.data')}</h4>
          <button className="btn btn-success" onClick={downloadCSV}>
            {t('common.downloadCsv')}
          </button>
        </div>
        <PaginationControls {...pagination} />
        <ErrorLoading error={error} loading={loading} />
        {sortedData && Array.isArray(sortedData) && !loading ? (
          <div className="card shadow-lg p-4">
            <div className="table-responsive">
              <table className="table table-striped table-bordered table-hover">
                <thead>
                  <tr>
                    <th>{t('events.homeHandicap')}</th>
                    <th>{t('events.awayHandicap')}</th>
                    <th>{t('events.homeOdds')}</th>
                    <th>{t('events.awayOdds')}</th>
                    <th>{t('events.home1x2Odds')}</th>
                    <th>{t('events.draw1x2Odds')}</th>
                    <th>{t('events.away1x2Odds')}</th>
                    <th>{t('events.halfHomeHandicap')}</th>
                    <th>{t('events.halfAwayHandicap')}</th>
                    <th>{t('events.halfHomeOdds')}</th>
                    <th>{t('events.halfAwayOdds')}</th>
                    <th>{t('events.totalGoalOver15')}</th>
                    <th>{t('events.totalGoalUnder15')}</th>
                    <th>{t('events.totalGoalOver25')}</th>
                    <th>{t('events.totalGoalUnder25')}</th>
                    <th>{t('events.totalGoalOver35')}</th>
                    <th>{t('events.totalGoalUnder35')}</th>
                    {sortableHeader(t('common.createdAt'), 'created_at')}
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item, index) => (
                    <tr key={index}>
                      <td>{item.home_handicap ?? ''}</td>
                      <td>{item.away_handicap ?? ''}</td>
                      <td>{item.home_price ?? ''}</td>
                      <td>{item.away_price ?? ''}</td>
                      <td>{item.home_1x2_price ?? ''}</td>
                      <td>{item.draw_1x2_price ?? ''}</td>
                      <td>{item.away_1x2_price ?? ''}</td>
                      <td>{item.home_half_goal_handicap ?? ''}</td>
                      <td>{item.away_half_goal_handicap ?? ''}</td>
                      <td>{item.home_half_goal_price ?? ''}</td>
                      <td>{item.away_half_goal_price ?? ''}</td>
                      <td>{item.total_goal_over_price_1_point_5 ?? ''}</td>
                      <td>{item.total_goal_under_price_1_point_5 ?? ''}</td>
                      <td>{item.total_goal_over_price_2_point_5 ?? ''}</td>
                      <td>{item.total_goal_under_price_2_point_5 ?? ''}</td>
                      <td>{item.total_goal_over_price_3_point_5 ?? ''}</td>
                      <td>{item.total_goal_under_price_3_point_5 ?? ''}</td>
                      <td>{formatDateTime(item.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          !loading && !error && <p className="text-muted">{t('common.noData')}</p>
        )}
      </div>
    </div>
  );
}

export default EventDetails;
